import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Helmet } from 'react-helmet';
import moment from 'moment';

import { CardContainer, CardWithModal, TitleAndEditButton } from 'shared-components';

import { ApplicationState } from '../../../../../store';
import * as StudentActions from '../../../../../store/student/actions';
import * as VPFActions from '../../../../../store/student/visaPrepForm/actions';
import QuestionPage from '../../../../templates/question';
import { VQ } from '../../../../../store/student/types';
import {
  createNavigateBackwardAction,
  createNavigateForwardAction,
  createNavigateToAction
} from '../../../../../middleware/actions';
import BorderRefusals from './borderRefusals';
import { IBorderRefusal } from '../../../../../types';
import { Pages } from '../../../../../store/student/visaPrepForm/types';
import { getPageStatus } from './utils';
import { toggleTaskMenu } from '../../../../../store/ui/actions';
import { titleAppend } from '../../../../../utilities/head';

const VisaPrepFormAddHistoryPage6 = () => {
  // dispatch actions
  const dispatch = useDispatch();
  const onPrevious = () => dispatch(createNavigateBackwardAction());
  const onNext = () => {
    dispatch(StudentActions.saveVisaPrepForm(Pages.PAGE2_6));
    dispatch(createNavigateForwardAction());
  };
  const onSkip = () => dispatch(createNavigateForwardAction());
  const onChange = (fieldName: string, value: any, refValue?: any) =>
    dispatch(VPFActions.createUpdateVPIFieldAction(fieldName, value, refValue));
  const onBackToTaskList = () => {
    dispatch(createNavigateToAction('/dashboard'));
    dispatch(toggleTaskMenu(false));
  };
  const onEdit = () => dispatch(createNavigateToAction('/history/step-2'));

  // state props from store
  const defaultArr = useMemo(() => {
    return [];
  }, []);
  const borderRefusals =
    useSelector(({ student }: ApplicationState) => student.visa.borderRefusals, shallowEqual) || defaultArr;

  // constants
  const currentStep = 6;
  const steps = 8;
  const sectionTitle = 'Additional History';

  // component state
  const [complete, setComplete] = useState(false);
  const [borderRefusalsState, setBorderRefusalsState] = useState<IBorderRefusal[]>([]);
  const [showModal, setShowModal] = useState<boolean[]>([]);

  useEffect(() => {
    setBorderRefusalsState([
      ...borderRefusals,
      {
        airport: '',
        reason: '',
        reference: '',
        refusalDate: ''
      }
    ]);
    setShowModal([...borderRefusals.map(_ => false), false]);
  }, [borderRefusals]);

  useEffect(() => {
    const { complete: newComplete } = getPageStatus({
      borderRefusals
    });
    if (newComplete !== complete) {
      setComplete(newComplete);
    }
  }, [complete, borderRefusals]);

  const toggleModal = (index: number) => {
    setShowModal(showModal.map((value, i) => (i === index ? !value : value)));
  };

  const deleteRefusal = (index: number): void => {
    onChange(VQ.BORDER_REFUSALS, [...borderRefusals.slice(0, index), ...borderRefusals.slice(index + 1)]);
  };

  return (
    <QuestionPage
      complete={complete}
      currentStep={currentStep}
      steps={steps}
      onPrevious={onPrevious}
      onNext={onNext}
      onSkip={onSkip}
      onBackToTaskList={onBackToTaskList}
      section={sectionTitle}
    >
      <Helmet>
        <title>
          {`${sectionTitle}: Step ${currentStep}`} {titleAppend}
        </title>
      </Helmet>

      <TitleAndEditButton
        title="You indicated that you have previously received a UK border refusal"
        onClick={onEdit}
      />

      <CardContainer title={`Number of border refusals`}>
        {borderRefusalsState.map((refusal: IBorderRefusal, index: number) => (
          <CardWithModal
            key={index}
            id={`border-refusals-${index}`}
            index={index}
            modalTitle={'Add Border Refusal'}
            cardItems={[
              {
                label: 'Date of refusal',
                value: moment(refusal.refusalDate).format('DD MMM YYYY')
              },
              {
                label: 'Port or airport',
                value: refusal.airport
              },
              {
                label: 'Reason',
                value: refusal.reason
              },
              {
                label: 'Reference number',
                value: refusal.reference
              }
            ]}
            onRemove={deleteRefusal}
            addButtonLabel={`Add Border Refusal`}
            addAnotherButtonLabel={`I have another Border Refusal to add`}
            hasAddMoreButton={
              refusal.airport === '' && refusal.reason === '' && refusal.reference === '' && refusal.refusalDate === ''
            }
            showModal={showModal[index]}
            toggleModal={toggleModal}
          >
            <BorderRefusals
              index={index}
              airport={refusal.airport}
              reason={refusal.reason}
              reference={refusal.reference}
              refusalDate={refusal.refusalDate}
              onSaveAndClose={(refusal: IBorderRefusal) => {
                const refusals = [...borderRefusals];
                refusals[index] = {
                  ...refusal
                };
                onChange(VQ.BORDER_REFUSALS, refusals);
                toggleModal(index);
              }}
            />
          </CardWithModal>
        ))}
      </CardContainer>
    </QuestionPage>
  );
};
export default VisaPrepFormAddHistoryPage6;
